import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Clock, CheckCircle2 } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import Breadcrumbs from "../../components/marketing/Breadcrumbs";
import CtaBanner from "../../components/marketing/CtaBanner";
import { getPost, BLOG_POSTS } from "../../data/blogData";
import { getFeature } from "../../data/featuresData";
import { getIndustry } from "../../data/industriesData";
import GuideCapture from "../../components/marketing/GuideCapture";

const fmtDate = (d) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ];
  const relatedFeatures = (post.relatedFeatures || []).map(getFeature).filter(Boolean);
  const relatedIndustries = (post.relatedIndustries || []).map(getIndustry).filter(Boolean);
  const morePosts = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const postJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: `${SITE_URL}${post.img}`,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Fieldz AI" },
    publisher: { "@type": "Organization", name: "Fieldz AI", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-dark.png` } },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={`${post.title} | Fieldz AI Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={`${SITE_URL}/marketing/og/og-blog.jpg`}
        jsonLd={[breadcrumbJsonLd(crumbs), postJsonLd]}
      />
      <MarketingNav />

      {/* Header */}
      <section className="pt-24 md:pt-32 pb-10 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Breadcrumbs items={[crumbs[0], crumbs[1], { name: post.category, path: `/blog/${post.slug}` }]} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2.5 py-1 rounded-full bg-[var(--mk-blue-light)] text-[var(--mk-blue)] text-[11px] font-bold uppercase tracking-wide">{post.category}</span>
            <span className="text-[13px] text-slate-400">{fmtDate(post.date)}</span>
            <span className="flex items-center gap-1 text-[13px] text-slate-400 font-medium">
              <Clock className="w-3.5 h-3.5" /> {post.readMins} min read
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-extrabold tracking-tight text-slate-900 leading-[1.15] mb-5" data-testid="blog-post-h1">
            {post.title}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Hero image */}
      <section className="pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-[0_20px_40px_-12px_rgba(15,23,42,0.15)]">
            <img src={post.img} alt={post.imgAlt} className="w-full block" width={1440} height={900} />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="pb-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.sections.map((s) => (
            <section key={s.h2} className="mb-10">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-4">{s.h2}</h2>
              {s.paras.map((p, i) => (
                <p key={i} className="text-[16px] text-slate-600 leading-[1.85] mb-4">{p}</p>
              ))}
            </section>
          ))}

          {/* Takeaways */}
          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6 sm:p-7 mb-10" data-testid="blog-takeaways">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Key takeaways</h2>
            <ul className="space-y-2.5">
              {post.takeaways.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[15px] text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[var(--mk-blue)] shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Lead magnet */}
          <GuideCapture source={post.slug} />

          {/* Related features & industries */}
          {(relatedFeatures.length > 0 || relatedIndustries.length > 0) && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[14px] font-semibold text-slate-500">Mentioned in this article:</span>
              {relatedFeatures.map((f) => (
                <Link
                  key={f.slug}
                  to={`/features/${f.slug}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-slate-200 bg-white text-[13.5px] font-semibold text-slate-700 transition-all hover:border-blue-300 hover:text-[var(--mk-blue)]"
                  data-testid={`blog-related-feature-${f.slug}`}
                >
                  <f.icon className="w-3.5 h-3.5 text-[var(--mk-blue)]" /> {f.name}
                </Link>
              ))}
              {relatedIndustries.map((ind) => (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-emerald-200 bg-emerald-50/60 text-[13.5px] font-semibold text-emerald-800 transition-all hover:border-emerald-400"
                  data-testid={`blog-related-industry-${ind.slug}`}
                >
                  <ind.icon className="w-3.5 h-3.5 text-emerald-600" /> {ind.name} Software
                </Link>
              ))}
            </div>
          )}
        </div>
      </article>

      {/* More posts */}
      <section className="py-14 bg-[var(--mk-bg-2)] border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-8">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {morePosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group block rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)] hover:border-blue-200"
                data-testid={`more-post-${p.slug}`}
              >
                <span className="inline-block px-2.5 py-1 rounded-full bg-[var(--mk-blue-light)] text-[var(--mk-blue)] text-[11px] font-bold uppercase tracking-wide mb-3">{p.category}</span>
                <h3 className="text-[16px] font-semibold text-slate-900 leading-snug mb-3">{p.title}</h3>
                <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--mk-blue)]">
                  Read <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
      <MarketingFooter />
    </div>
  );
}
